<template>
    <div class="registration-form">
        <div class="wrapper">
            <div class="form-header">
                <div class="form-title">
                    <ButtonRound
                        v-if="backButtonInTitle && page === 'EMAIL_AND_NAME'"
                        class="button-back-title"
                        icon="angle-left"
                        type="link"
                        aria-label="Go back one level"
                        @click="goToPageSso"
                    />

                    <slot name="title"></slot>
                </div>

                <div
                    v-if="$slots['description']"
                    class="form-description"
                >
                    <slot name="description"></slot>
                </div>
            </div>

            <form
                class="form"
                name="register"
                @submit.prevent="handleFormSubmitEvent"
            >
                <fieldset
                    v-show="page === 'SSO'"
                    class="fieldset sso"
                    :disabled
                >
                    <div class="sso-wrapper">
                        <GoogleSignIn
                            @submit="handleGoogleSignInSuccess"
                            @fail="handleGoogleSigninFailure"
                            @click="trackClickRegistration('google')"
                        />

                        <FacebookSignIn
                            @submit="handleFacebookSignInSuccess"
                            @fail="handleFacebookSigninFailure"
                            @click="trackClickRegistration('facebook')"
                        />
                    </div>

                    <div class="separator">
                        <span class="text">or</span>
                    </div>

                    <div v-if="!hideInputsIfLogged || !isLogged">
                        <TextField
                            id="form-email"
                            v-model="email"
                            v-qa="page === 'SSO' ? 'registration-email' : ''"
                            :error="continueWithEmailErrorMessage"
                            :placeholder="t('registration.email')"
                            lazy
                            required
                        />

                        <Honeypot v-model="honeypot" />
                    </div>

                    <slot name="contact"></slot>

                    <FormActionErrors :errors="errorsWithFeValidation" />

                    <ThButton
                        v-qa="'registration-email-continue'"
                        full-width
                        class="th-button"
                        type="solid"
                        @click="isLogged ? submit() : continueWithEmail()"
                    >
                        <slot name="submit">
                            {{ t('registration.continue') }}
                        </slot>
                    </ThButton>

                    <div v-if="!hideInputsIfLogged || !isLogged">
                        <ThCheckbox
                            v-model="emailConsent"
                            :error="
                                errorsWithFeValidation.last(
                                    'customer.emailConsent'
                                )
                            "
                        >
                            {{ t('registration.email_consent_label') }}
                        </ThCheckbox>
                    </div>
                </fieldset>

                <fieldset
                    v-show="page === 'EMAIL_AND_NAME'"
                    :disabled
                    class="fieldset email-and-name"
                >
                    <TextField
                        id="form-email"
                        v-model="email"
                        v-qa="
                            page === 'EMAIL_AND_NAME'
                                ? 'registration-email'
                                : ''
                        "
                        :error="errorsWithFeValidation.last('customer.email')"
                        :placeholder="t('enquiry.email')"
                        lazy
                        required
                    />

                    <TextField
                        id="form-first-name"
                        v-model="names"
                        v-qa="'registration-first-name'"
                        :error="
                            errorsWithFeValidation.last('customer.firstName')
                        "
                        :placeholder="t('registration.name')"
                        lazy
                        required
                    />

                    <FormActionErrors :errors="errorsWithFeValidation" />

                    <ThButton
                        v-qa="'submit'"
                        full-width
                        submit
                        :loading="disabled"
                        class="th-button"
                        type="solid"
                    >
                        <slot name="submit">
                            {{ t('registration.continue') }}
                        </slot>
                    </ThButton>

                    <div v-if="!hideInputsIfLogged || !isLogged">
                        <ThCheckbox
                            v-model="emailConsent"
                            :error="
                                errorsWithFeValidation.last(
                                    'customer.emailConsent'
                                )
                            "
                        >
                            {{ t('registration.email_consent_label') }}
                        </ThCheckbox>
                    </div>
                </fieldset>
            </form>

            <div
                v-show="page === 'EMAIL_AND_NAME'"
                class="signin-switch"
            >
                {{ t('registration.switch_to_signin.label') }}

                <button
                    class="button"
                    @click="switchToLogin"
                >
                    {{ t('registration.switch_to_signin.button') }}
                </button>
            </div>
        </div>

        <ConsentNotice class="consent-notice" />

        <ButtonRound
            v-if="!backButtonInTitle && page === 'EMAIL_AND_NAME'"
            class="button-back"
            icon="angle-left"
            type="link"
            aria-label="Go back one level"
            @click="goToPageSso"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'

import { useAuth } from '@/plugins/auth.ts'
import type { ErrorCollectionWithTraces } from '@/helpers/errors'
import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import { useEmailAndNamesFieldsError } from './useEmailAndNamesFieldsError'
import { useEmailFieldError } from './useEmailFieldError'
import { useAuthTracking } from './useAuthTracking'

import TextField from '@/components/ui/inputs/TextField.vue'
import ThCheckbox from '@/components/ui/inputs/ThCheckbox.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import FormActionErrors from '@/components/ui/form/error/FormActionErrors.vue'
import ConsentNotice from './ConsentNotice.vue'
import GoogleSignIn from './sso/GoogleSignIn.vue'
import FacebookSignIn from './sso/FacebookSignIn.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import Honeypot from './Honeypot.vue'

interface Props {
    errors: ErrorCollectionWithTraces
    disabled?: boolean
    hideInputsIfLogged?: boolean
    backButtonInTitle?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    isContactUsForm: false,
})

const emit = defineEmits<{
    submit: [successCallback: () => void]
    switchToLogin: []
}>()

const { t } = useI18n()

const {
    trackSso,
    trackEmail,
    trackClick,
    trackAuthNavigation,
    trackMarketingOptInToggle,
    trackHoneypot,
} = useAuthTracking()

const { email, emailConsent, names, validatedEmail } = storeToRefs(
    useCustomerRegistrationInputStore()
)

const trackClickRegistration = (provider: Parameters<typeof trackClick>[0]) =>
    trackClick(provider, 'registration')

watch(emailConsent, (val) => trackMarketingOptInToggle(val))

const emailError = computed(() =>
    errorsWithFeValidation.value.last('customer.email')
)

const { isLogged } = useAuth()

const emitSubmit = (trackSubmitSuccessCallback: () => void) =>
    emit('submit', trackSubmitSuccessCallback)

const handleGoogleSignInSuccess = () => {
    emitSubmit(() => trackSso('google', 'success', 'registration'))
}

const handleGoogleSigninFailure = () => {
    trackSso('google', 'failure', 'registration')
}

const handleFacebookSignInSuccess = () => {
    emitSubmit(() => trackSso('facebook', 'success', 'registration'))
}

const handleFacebookSigninFailure = () => {
    trackSso('facebook', 'failure', 'registration')
}

const handleEmailSubmit = () => {
    emitSubmit(() => trackEmail('success', 'registration'))
}

const { submit, errorsWithFeValidation } = useEmailAndNamesFieldsError(
    isLogged,
    toRef(() => props.errors),
    names,
    email,
    validatedEmail,
    handleEmailSubmit
)

const handleFormSubmitEvent = () => {
    if (page.value === 'EMAIL_AND_NAME') {
        submit()
    } else if (page.value === 'SSO') {
        if (email.value && !emailError.value) {
            continueWithEmail()
        }
    }
}

const honeypot = ref('')

const goToPageEmailAndName = () => {
    if (honeypot.value) {
        trackHoneypot('registration')

        return
    }

    page.value = 'EMAIL_AND_NAME'

    trackClickRegistration('email')
}

const {
    errorsWithFeValidation: continueWithEmailError,
    submit: continueWithEmail,
} = useEmailFieldError(email, validatedEmail, goToPageEmailAndName)

const continueWithEmailErrorMessage = computed(() =>
    continueWithEmailError.value.last('customer.email')
)

const page = ref<'SSO' | 'EMAIL_AND_NAME'>('SSO')

const goToPageSso = () => {
    page.value = 'SSO'
}

const switchToLogin = () => {
    trackAuthNavigation('login')

    emit('switchToLogin')
}
</script>

<style lang="scss" scoped>
.registration-form {
    height: 100%;

    .wrapper {
        width: 100%;
        height: 100%;
        max-width: 500px;
        margin: 0 auto;

        position: relative;
        display: flex;
        flex-direction: column;
        align-self: flex-start;
        justify-content: flex-start;

        @include min-width(410) {
            justify-content: center;
        }

        font-family: $sectra-font-stack;
        font-style: normal;
        font-weight: 400;
        color: $slate;
        font-size: 0.75rem;
        line-height: 1.4em;
    }

    .form-header {
        width: 100%;
        padding: 0 20px;

        margin: 0 auto;

        @include min-width(520) {
            margin: 0 auto 40px;
        }

        .form-title {
            font-family: $parcel-font-stack;
            font-size: 2rem;
            line-height: 1em;
            color: $blue;
            text-align: center;
            margin: 0 0 30px;

            .button-back-title {
                display: block;
                align-self: start;
            }
        }

        .form-description {
            text-align: center;
            line-height: 1.4em;
            margin-bottom: 16px;
        }
    }

    .form {
        padding: 0 20px 50px;

        margin: 0 auto;
        width: 100%;
        max-width: 380px;

        .fieldset {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;

            > * {
                width: 100%;
            }
        }

        .fieldset:disabled {
            opacity: 0.5;
        }

        .sso-wrapper {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 16px;
            width: 100%;
        }

        .separator {
            text-align: center;
            display: flex;
            align-items: center;
            max-width: 200px;
            margin: 0 auto;
            text-transform: uppercase;
            font-family: $effra-font-stack;
            letter-spacing: 0.05em;
            font-size: 0.875rem;

            &:before,
            &:after {
                content: '';
                display: inline-block;
                width: 50%;
                height: 1px;
                background-color: $grey-300;
            }

            .text {
                display: block;
                margin: 0 10px;
            }
        }
    }

    .signin-switch {
        position: absolute;
        bottom: 16px;
        width: 100%;
        text-align: center;

        .button {
            position: relative;
            color: $blue;
            cursor: pointer;

            &::after {
                position: absolute;
                top: 0.5px;
                font-family: $icon-font;
                content: $icon-angle-right-bold;
                font-size: 12px;
            }
        }
    }

    .button-back {
        position: absolute;
        top: 15px;
        left: 20px;
        z-index: 1;
    }
}
</style>
